import TomClient from "../../anwendungskonfigurator/services/tomClient"
import {errorParser} from "../../anwendungskonfigurator/misc/utils"
import {
    PARTNER_LINK_VORLAGEN,
    partnerKundeLinks,
} from "../../anwendungskonfigurator/misc/partner-links"



const LINKS = "/links"
const SUCHE = "/partnersuche"
const RESULT = "/partnersuche/result"
const SUCHE_ROUTE = "/partner/suche"


const ANLAGE = {
    np: {
        area: "PartnerNp",
        route: "/partner/np/neu",
        pointer: "/partner/np/basisdaten",
        links: {get: "getPartnerNp", update: "updatePartnerNp"},
        felder: [
            "anrede",
            "titel",
            "vorname",
            "nachname",
            "geburtsdatum",
            "familienstand",
            "beruflicherStatus",
        ],
        pflicht: {feld: "nachname", frage: "Nachname"},
        ausSuche: ["nachname"],
    },
    jp: {
        area: "PartnerJp",
        route: "/partner/jp/neu",
        pointer: "/partner/jp/stammdaten",
        links: {get: "getPartnerJp", update: "updatePartnerJp"},
        felder: ["firmenname", "rechtsform", "kundennummer"],
        pflicht: {feld: "firmenname", frage: "Firmenname"},
        ausSuche: ["firmenname", "kundennummer"],
    },
}

const MODUS = "/partneranlage/modus"
const MODUS_NEU = "NEU"
const MODUS_BEARBEITEN = "BEARBEITEN"

const gefuellt = (wert) => String(wert ?? "").trim() !== ""

const DISCRIMINATORS = {np: "np", jp: "jp"}


export const partnerLinksOf = (vorlagen, resource, discriminator) => {
    const links = resource?._links ?? {}
    const area = discriminator === DISCRIMINATORS.jp ? "PartnerJp" : "PartnerNp"

    const normalized = partnerKundeLinks(vorlagen, discriminator, resource?.modelId)

    if (links._self?.href) {
        normalized[`get${area}`] = {href: links._self.href, methods: ["GET"]}
    }
    if (links["kunde:update"]?.href) {
        normalized[`update${area}`] = {
            href: links["kunde:update"].href,
            methods: links["kunde:update"].methods ?? ["PUT"],
        }
    }
    for (const name of ["adresseList", "adresse:create", "adresseUpdate"]) {
        if (links[name]?.href) normalized[name] = links[name]
    }
    return normalized
}

export const partnerRoute = (discriminator, modelId) => `/partner/${discriminator}/${modelId}`

export const PartnerActions = ({
    getDataFromPointer,
    updateData,
    navigate,
    triggerAction,
}) => {
    const mergeLinks = (links) =>
        updateData({...(getDataFromPointer(LINKS) ?? {}), ...links}, LINKS)

    
    const meldung = (text) =>
        errorParser(updateData, SUCHE)({_messages: [{severity: "ERROR", paths: [], text}]})

    
    const call = (name, hallink, options) => {
        if (!hallink?.href) {
            return Promise.reject(
                new Error(
                    `[${name}] HAL-Link ohne href — der Aufruf wurde nicht abgeschickt. ` +
                        `Gelesen wurde: ${JSON.stringify(hallink)}`,
                ),
            )
        }
        return TomClient.callBackend(hallink, options)
    }

    const onError = (kontext) => (error) => {
        console.error(`[${kontext}] Aufruf ans Partner-Backend fehlgeschlagen`, error)
        if (error?._messages) return errorParser(updateData, SUCHE)(error)
        return Promise.resolve()
    }

    
    const uebernehmen = (resource, discriminator) => {
        if (!resource?.modelId) {
            console.error(
                resource === undefined
                    ? "[PARTNER] keine Antwort vom Partner-Backend — der Aufruf ist fehlgeschlagen"
                    : "[PARTNER] Kunde ohne modelId — kann nicht geoeffnet werden",
                resource,
            )
            return Promise.resolve()
        }
        mergeLinks(
            partnerLinksOf(getDataFromPointer(PARTNER_LINK_VORLAGEN), resource, discriminator),
        )
        updateData(MODUS_BEARBEITEN, MODUS)
        navigate(partnerRoute(discriminator, resource.modelId))

        triggerAction?.("GET_CONTEXTAREA", {contextArea: ANLAGE[discriminator].area})
        return Promise.resolve(resource)
    }

    const suchen = (discriminator) => {
        const linkName = discriminator === DISCRIMINATORS.jp ? "partnerSucheJp" : "partnerSucheNp"
        const hallink = getDataFromPointer(`${LINKS}/${linkName}`)
        if (!hallink?.href) {
            console.warn(
                `[PARTNER_SUCHE] '${linkName}' fehlt in /links —`,
                "die Partner-Links holt INIT_ROUTING beim tom-server (GET /partner/links)",
            )
            return Promise.resolve()
        }
        const query = getDataFromPointer(`${SUCHE}/${discriminator}`) ?? {}
        return call(`PARTNER_SUCHE_${discriminator.toUpperCase()}`, hallink, {query})
            .then((response) => updateData(response?._data ?? [], `${RESULT}/${discriminator}`))
            .catch(onError(`PARTNER_SUCHE_${discriminator.toUpperCase()}`))
    }

    const kundenLinksEntfernen = (spec) => {
        const links = {...(getDataFromPointer(LINKS) ?? {})}
        delete links[spec.links.get]
        delete links[spec.links.update]
        updateData(links, LINKS)
    }

    const startwerteAusSuche = (spec, discriminator) => {
        const formular = getDataFromPointer(`${SUCHE}/${discriminator}`) ?? {}
        return spec.ausSuche.reduce((gesammelt, feld) => {
            if (gefuellt(formular[feld])) gesammelt[feld] = formular[feld]
            return gesammelt
        }, {})
    }

    const anlegeStart = (discriminator) => {
        const spec = ANLAGE[discriminator]
        kundenLinksEntfernen(spec)
        updateData(startwerteAusSuche(spec, discriminator), spec.pointer)
        updateData(MODUS_NEU, MODUS)
        navigate(spec.route)
        return Promise.resolve()
    }

    
    const seiteInit = (discriminator) => {
        const spec = ANLAGE[discriminator]
        const neu = String(globalThis.location?.pathname ?? "").endsWith("/neu")
        updateData(neu ? MODUS_NEU : MODUS_BEARBEITEN, MODUS)

        if (neu && getDataFromPointer(`${LINKS}/${spec.links.get}`)?.href) {
            kundenLinksEntfernen(spec)
            updateData(startwerteAusSuche(spec, discriminator), spec.pointer)
        }
        return Promise.resolve()
    }

    const anlegen = (discriminator) => {
        const spec = ANLAGE[discriminator]
        const gross = discriminator === DISCRIMINATORS.jp ? "Jp" : "Np"
        const linkName = `partnerCreate${gross}`
        const hallink = getDataFromPointer(`${LINKS}/${linkName}`)
        if (!hallink?.href) {
            console.warn(
                `[PARTNER_ANLEGEN] '${linkName}' fehlt in /links —`,
                "die Partner-Links holt INIT_ROUTING beim tom-server (GET /partner/links)",
            )
            return Promise.resolve()
        }

        const formular = getDataFromPointer(spec.pointer) ?? {}

        if (!gefuellt(formular[spec.pflicht.feld])) {
            return meldung(
                `Bitte ${spec.pflicht.frage} eingeben, bevor ein Partner angelegt wird.`,
            )
        }

        const body = spec.felder.reduce((gesammelt, feld) => {
            if (gefuellt(formular[feld])) gesammelt[feld] = formular[feld]
            return gesammelt
        }, {})

        return call(
            `PARTNER_ANLEGEN_${discriminator.toUpperCase()}`,
            {href: hallink.href, methods: hallink.methods ?? ["POST"]},
            {body},
        )
            .then((response) => uebernehmen(response?._data, discriminator))
            .catch(onError(`PARTNER_ANLEGEN_${discriminator.toUpperCase()}`))
    }

    return {
        
        PARTNER_SUCHE_INIT: () => {
            updateData({}, RESULT)
            updateData({}, `${SUCHE}/np`)
            updateData({}, `${SUCHE}/jp`)
            if (!getDataFromPointer(`${LINKS}/partnerSucheNp`)?.href) {
                console.warn(
                    "[PARTNER_SUCHE_INIT] keine Partner-Links in /links —",
                    "lief INIT_ROUTING, und kennt der tom-server die Partner-Basis?",
                )
            }
            return Promise.resolve()
        },

        PARTNER_SUCHE_NP: () => suchen(DISCRIMINATORS.np),
        PARTNER_SUCHE_JP: () => suchen(DISCRIMINATORS.jp),

        PARTNER_LADEN_NP: (payload) =>
            uebernehmen(payload?.payload ?? payload, DISCRIMINATORS.np),
        PARTNER_LADEN_JP: (payload) =>
            uebernehmen(payload?.payload ?? payload, DISCRIMINATORS.jp),

        
        PARTNER_ANLEGEN_START_NP: () => anlegeStart(DISCRIMINATORS.np),
        PARTNER_ANLEGEN_START_JP: () => anlegeStart(DISCRIMINATORS.jp),

        PARTNER_SEITE_INIT_NP: () => seiteInit(DISCRIMINATORS.np),
        PARTNER_SEITE_INIT_JP: () => seiteInit(DISCRIMINATORS.jp),

        PARTNER_ANLEGEN_NP: () => anlegen(DISCRIMINATORS.np),
        PARTNER_ANLEGEN_JP: () => anlegen(DISCRIMINATORS.jp),

        PARTNER_ANLEGEN_ZURUECK: () => {
            navigate(SUCHE_ROUTE)
            return Promise.resolve()
        },
    }
}
